<template>
  <div>
    <page-header :title="title" :breadcrumb="breadcrumb" :container-class="containerClass" />
    <div class="page-section">
      <div :class="containerClass">
        <page-separator :title="title" />

        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <!-- Search -->
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search Programs"
                    v-model="searchTerm"
                    @input="onSearch"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon>search</md-icon>
                  </b-btn>
                </form>
              </div>

              <!-- Filters and Add Btn -->
              <div class="col-lg d-flex flex-wrap align-items-center justify-content-end">
                <b-form-select
                  v-model="selectedType"
                  :options="typeOptions"
                  class="w-auto mr-8pt mb-8pt mb-lg-0"
                  style="min-width: 180px"
                />

                <b-btn :to="{ name: 'sa-add-program' }" exact variant="primary">Add Program</b-btn>
              </div>
            </div>
          </div>

          <!-- Schools Table -->
          <b-table
            :fields="tableColumns"
            :items="filteredPrograms"
            :busy="isLoading"
            :per-page="perPage"
            :current-page="currentPage"
            head-variant="light"
            class="table-nowrap"
            hover
            responsive
            no-local-sorting
            @sort-changed="onSortChange"
            @row-clicked="onRowClicked"
          >
            <template #head(actions)="">
              <span></span>
            </template>
            <template #cell(actions)="data">
              <b-dropdown :boundary="'main'" no-flip variant="flush" toggle-class="text-muted" no-caret>
                <template #button-content>
                  <i class="material-icons icon-24pt text-info">more_vert</i>
                </template>
                <b-dropdown-item
                  :to="{
                    name: 'sa-edit-program',
                    params: { id: data.item.id },
                  }"
                >
                  <md-icon class="text-info mr-2">edit</md-icon>
                  {{ $t('generalMsgs.edit') }}
                </b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item @click="confirmDelete(data.item.id)">
                  <md-icon class="text-danger mr-2">delete</md-icon>
                  {{ $t('generalMsgs.remove') }}
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>

          <!-- Footer Pagination -->
          <div class="card-footer">
            <pagination
              v-model="currentPage"
              :total-rows="filteredPrograms.length"
              :per-page="perPage"
              @change="onPageChange"
              aria-controls="schools-table"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { debounce } from 'lodash';
import { mapActions } from 'vuex';

import Page from '@/components/Page.vue';
import Pagination from '../../../components/Ui/Pagination.vue';
import MdIcon from '../../../components/Ui/MdIcon.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';

export default {
  components: { MdIcon, PageHeader, PageSeparator, Pagination },
  extends: Page,

  data() {
    return {
      title: 'Manage Programs',

      isLoading: false,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalPrograms: 0,
      programs: [],

      searchTerm: '',
      selectedType: 'certificate',
      typeOptions: [
        { value: 'all', text: 'All Types' },
        { value: 'certificate', text: 'Certificate' },
        { value: 'advanced_certificate', text: 'Advanced Certificate' },
        { value: 'license', text: 'License' },
        { value: 'degree', text: 'Degree' },
        { value: 'prep_course', text: 'Prep Course' },
        { value: 'pre_health', text: 'Pre-Health' },
        { value: 'continuing_education', text: 'Continuing Education' },
        { value: 'non_career', text: 'Non-Career' },
        { value: 'extracurricular', text: 'Extracurricular' },
      ],
    };
  },

  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: this.$t('Programs'), active: true },
      ];
    },

    filteredPrograms() {
      if (this.selectedType === 'all') return this.programs;
      return this.programs.filter(p => p.prg_type === this.selectedType);
    },

    tableColumns() {
      return [
        {
          key: 'actions',
          label: '',
          tdClass: 'text-left',
          thStyle: { minWidth: '100px' },
        },
        { key: 'title', label: 'Title' },
        { key: 'description', label: 'Description' },
      ];
    },
  },

  watch: {
    selectedType() {
      this.currentPage = 1;
    },
  },

  methods: {
    ...mapActions('program', ['getAllPrograms', 'deleteProgram']),
    async confirmDelete(id) {
      const h = this.$createElement;
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          h('div', [
            h('div', 'Removing a program will have following implications:'),
            h('br'),
            h('li', `All associated jobs will belong to 'others' category.`),
            h('li', `Enrolled student program will belong to 'others' category.`),
            h('li', `All associated Partnerships will have their program removed.`),
            h('li', `All associated Sponsorships will have their program removed.`),
          ]),
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          },
        );
        if (isConfirmed) {
          await this.deleteProgram(id);
          // const refreshPage = this.careers.length > 1 ? this.currentPage : this.currentPage - 1;
          this.fetchPrograms();
          this.makeToast({ variant: 'success', msg: 'Program Successfully Deleted' });
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    onRowClicked(data) {
      this.$router.push({ name: 'sa-edit-program', params: { id: data.id } });
    },

    async fetchPrograms(params = {}) {
      this.isLoading = true;
      document.getElementById('app').scrollIntoView();

      const response = await this.getAllPrograms({
        // limit: this.perPage,
        // offset: (pageNum - 1) * this.perPage,
        // ...(this.ordering && { ordering: this.ordering }),
        ...(this.searchTerm && { search: this.searchTerm }),
        ...params,
        ordering: 'id',
      });
      this.programs = response.data;
      this.totalPrograms = this.programs.length;
      this.currentPage = 1;
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.currentPage = pageNum;
      document.getElementById('app').scrollIntoView();
    },

    onSortChange(context) {
      this.ordering = context.sortDesc ? '-' + context.sortBy : context.sortBy;
      this.fetchPrograms();
    },

    onSearch() {
      this.debouncedSearchPrograms(this);
    },

    debouncedSearchPrograms: debounce(vm => {
      vm.fetchPrograms();
    }, 500),
  },

  async mounted() {
    this.fetchPrograms();
  },
};
</script>
